/**
 * Mobile Account Form Page
 * 
 * Full-page mobile form for creating and editing accounts.
 * Similar to CreateTransactionMobile but for accounts.
 */

import React, { ReactElement, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Account, Currency, AccountDetailValue } from "../types";
import { api } from "../services/api";
import { formatAmountToCurrencyDecimalPlaces } from "../utils/currencyDefaults";
import { useAccounts } from "./Accounts/hooks/useAccounts";
import { useAccountGroups } from "./Accounts/hooks/useAccountGroups";
import { useCurrencies } from "./Accounts/hooks/useCurrencies";
import { useAccountForm } from "./Accounts/hooks/useAccountForm";
import { useAccountHandlers } from "./Accounts/hooks/useAccountHandlers";
import DuplicateDetectionTextField from "../components/DuplicateDetectionTextField";
import InfoTooltipIcon from "../components/InfoTooltipIcon";
import CannotChangeLaterIcon from "../components/CannotChangeLaterIcon";
import MobileAccountSubmitButton from "./MobileAccounts/components/MobileAccountSubmitButton";
import { useMobileCalculator } from "./Activity/mobile/hooks/useMobileCalculator";
import MobileCalculator from "./Activity/mobile/components/MobileCalculator";
import MobileBottomListSelect from "../components/MobileBottomListSelect";

interface MobileAccountFormProps {
  workspaceId: string;
}

function MobileAccountForm({ workspaceId }: MobileAccountFormProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ workspaceSlug?: string; accountId?: string }>();
  const workspaceSlug = params.workspaceSlug;
  // App route is /:workspaceSlug/* so accountId is in the path, not in params
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isEditAccountRoute = pathSegments[1] === "edit-account";
  const accountId = isEditAccountRoute ? (pathSegments[2] || params.accountId) : params.accountId;

  const isEditMode = Boolean(accountId);
  const duplicateAccountId = (location.state as any)?.duplicateAccountId;
  
  // Data fetching
  const { accounts, loading: accountsLoading } = useAccounts(workspaceId);
  const accountGroups = useAccountGroups(workspaceId);
  const {
    currencies,
    defaultCurrency,
    valuesVisibilityMode,
    valuesVisible,
    setValuesVisible,
  } = useCurrencies(workspaceId);
  
  const accountHasTransactions: Record<string, boolean> = {}; // Simplified for now
  
  // Account handlers - will manage form state
  const accountHandlers = useAccountHandlers({
    workspaceId,
    accounts,
    accountHasTransactions,
    formatCurrency: (amount: number) => amount.toString(),
    setSnackbarElement: () => {},
    setSnackbarOpen: () => {},
  });
  
  // Extract form state from handlers
  const {
    formData,
    setFormData,
    editingAccount,
    setEditingAccount,
    initialFormData,
    setInitialFormData,
    showAccountDetails,
    setShowAccountDetails,
    initialShowAccountDetails,
    setInitialShowAccountDetails,
    accountNameError,
    setAccountNameError,
    isSavingAccount,
    setIsSavingAccount,
    creatingAccountName,
    setCreatingAccountName,
    handleSubmit,
  } = accountHandlers;
  
  // Account form logic
  const {
    getSelectedGroupDetails,
    handleDetailValueChange,
    hasFormChanged,
    isAccountFormValid,
  } = useAccountForm({
    formData,
    setFormData,
    editingAccount,
    initialFormData,
    showAccountDetails,
    initialShowAccountDetails,
    accountNameError,
    accountGroups,
  });
  
  // Calculator for amount field
  const {
    calculatorExpression,
    setCalculatorExpression,
    getCalculatorDisplayValue,
  } = useMobileCalculator();
  const [amountCalculatorOpen, setAmountCalculatorOpen] = useState(false);

  // Bottom list selection (group, currency, or detail name) instead of dropdowns on mobile
  const [bottomListOpen, setBottomListOpen] = useState<"group" | "currency" | string | null>(null);

  // Load account for edit mode or duplicate
  useEffect(() => {
    if (!workspaceId || accountsLoading) return;
    
    if (isEditMode && accountId && !editingAccount) {
      // Load account for editing
      const account = accounts.find(acc => acc.id === accountId);
      if (account) {
        const accountCurrency = account.currency || defaultCurrency;
        const formattedAmount = formatAmountToCurrencyDecimalPlaces(
          String(account.amount ?? 0),
          accountCurrency,
          currencies,
          defaultCurrency
        );
        setEditingAccount(account);
        setFormData({
          accountName: account.accountName,
          group: account.group || "",
          amount: formattedAmount,
          currency: accountCurrency,
          description: account.description || "",
          details: account.details || {},
        });
        setInitialFormData({
          accountName: account.accountName,
          group: account.group || "",
          amount: formattedAmount,
          currency: accountCurrency,
          description: account.description || "",
          details: account.details || {},
        });
        setShowAccountDetails(!!account.details && Object.keys(account.details).length > 0);
        setInitialShowAccountDetails(!!account.details && Object.keys(account.details).length > 0);
      }
    } else if (duplicateAccountId && !isEditMode) {
      // Load account for duplicating
      const account = accounts.find(acc => acc.id === duplicateAccountId);
      if (account) {
        const accountCurrency = account.currency || defaultCurrency;
        const formattedAmount = formatAmountToCurrencyDecimalPlaces(
          String(account.amount ?? 0),
          accountCurrency,
          currencies,
          defaultCurrency
        );
        setFormData({
          accountName: `${account.accountName} (Copy)`,
          group: account.group || "",
          amount: formattedAmount,
          currency: accountCurrency,
          description: account.description || "",
          details: account.details ? { ...account.details } : {},
        });
        setShowAccountDetails(!!account.details && Object.keys(account.details).length > 0);
      }
    }
  }, [workspaceId, accountId, duplicateAccountId, accounts, accountsLoading, isEditMode, editingAccount, defaultCurrency, currencies, setFormData, setEditingAccount, setInitialFormData, setShowAccountDetails, setInitialShowAccountDetails]);
  
  const handleBack = () => {
    navigate(`/${workspaceSlug}/accounts`);
  };
  
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    await handleSubmit(e);
    // Navigate back after successful submit
    setTimeout(() => {
      navigate(`/${workspaceSlug}/accounts`);
    }, 500);
  };
  
  if (accountsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "background.default" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          position: "sticky",
          top: 0,
          zIndex: 1500,
          bgcolor: "background.paper",
        }}
      >
        <IconButton onClick={handleBack} sx={{ p: 0.75 }}>
          <ArrowBackIcon sx={{ fontSize: "1.25rem" }} />
        </IconButton>
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 500 }}>
          {isEditMode ? "Edit Account" : "Create Account"}
        </Typography>
      </Paper>
      
      {/* Form */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2, pb: amountCalculatorOpen ? "480px" : "calc(100px + env(safe-area-inset-bottom, 0px))" }}>
        <form onSubmit={handleFormSubmit} id="account-form">
          <Stack spacing={2}>
            <TextField
              fullWidth
              required
              label="Group"
              value={formData.group}
              onClick={() => setBottomListOpen("group")}
              onFocus={(e) => {
                e.target.blur();
                setBottomListOpen("group");
              }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <ChevronRightIcon color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Select group"
            />

            <DuplicateDetectionTextField
              required
              label="Account Name"
              value={formData.accountName}
              onChange={(value) => {
                setFormData({ ...formData, accountName: value });
              }}
              onFocus={() => {
                setAmountCalculatorOpen(false);
                setBottomListOpen(null);
              }}
              existingItems={accounts
                .filter((acc) => {
                  if (isSavingAccount && creatingAccountName && !editingAccount) {
                    return acc.accountName.toLowerCase() !== creatingAccountName.toLowerCase();
                  }
                  return true;
                })
                .map((acc) => ({ 
                  name: acc.accountName, 
                  id: acc.id,
                  isArchived: acc.isArchived 
                }))}
              currentItemId={editingAccount?.id}
              itemType="account"
              errorMessageFormat={(itemName, itemType, item) => {
                const archivedMsg = item?.isArchived ? " (archived)" : "";
                return `An ${itemType} named "${itemName}"${archivedMsg} already exists. Please use a different name.`;
              }}
              onErrorChange={(error) => {
                if (!isSavingAccount) {
                  setAccountNameError(error);
                }
              }}
              fullWidth
              placeholder="Enter account name"
            />

            <TextField
              label="Initial Amount"
              type="text"
              inputProps={{ readOnly: true, inputMode: "none" }}
              value={amountCalculatorOpen ? getCalculatorDisplayValue() : (formData.amount || "")}
              onClick={(e) => {
                e.preventDefault();
                const raw = (formData.amount || "").trim() || "0";
                setCalculatorExpression(raw);
                setAmountCalculatorOpen(true);
              }}
              onFocus={(e) => {
                e.preventDefault();
                e.target.blur();
                const raw = (formData.amount || "").trim() || "0";
                setCalculatorExpression(raw);
                setAmountCalculatorOpen(true);
              }}
              fullWidth
              placeholder="0.00"
              helperText="Optional - tap to open calculator"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoTooltipIcon
                      tooltipMessage="You can still change the amount manually from here, but it will appear in activity and can't delete later."
                      position="inline"
                      size="small"
                      sx={{ ml: 0.5 }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                required
                label="Currency"
                value={
                  formData.currency === defaultCurrency
                    ? defaultCurrency
                    : (currencies.find((c) => c.code === formData.currency)?.name
                        ? `${formData.currency} - ${currencies.find((c) => c.code === formData.currency)?.name}`
                        : formData.currency)
                }
                onClick={() => !editingAccount && setBottomListOpen("currency")}
                onFocus={(e) => {
                  e.target.blur();
                  if (!editingAccount) setBottomListOpen("currency");
                }}
                disabled={!!editingAccount}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <ChevronRightIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                placeholder="Select currency"
              />
              <CannotChangeLaterIcon
                tooltipMessage="Currency cannot be changed after creation. You can define currencies and rates in Settings."
              />
            </Box>

            <TextField
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              onFocus={() => {
                setAmountCalculatorOpen(false);
                setBottomListOpen(null);
              }}
              fullWidth
              placeholder="Add a description..."
            />

            {/* Account Details Checkbox */}
            {formData.group && getSelectedGroupDetails().length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAccountDetails}
                    onChange={(e) => {
                      setShowAccountDetails(e.target.checked);
                    }}
                  />
                }
                label="Fill Account Details"
              />
            )}

            {/* Detail Fields */}
            {formData.group && getSelectedGroupDetails().length > 0 && showAccountDetails && (
              <Box sx={{ mt: 1 }}>
                <Stack spacing={2}>
                  {getSelectedGroupDetails().map((detail) => {
                    const currentValue = formData.details?.[detail.name] || "";
                    return (
                      <Box key={detail.name}>
                        {detail.type === "list" ? (
                          <TextField
                            fullWidth
                            label={detail.name}
                            value={currentValue || ""}
                            onClick={() =>
                              detail.listItems?.length
                                ? setBottomListOpen(`detail:${detail.name}`)
                                : undefined
                            }
                            onFocus={(e) => {
                              e.target.blur();
                              if (detail.listItems?.length)
                                setBottomListOpen(`detail:${detail.name}`);
                            }}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <ChevronRightIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            placeholder={
                              detail.listItems?.length ? "Select" : "No items available"
                            }
                          />
                        ) : detail.type === "number" ? (
                          <TextField
                            label={detail.name}
                            value={currentValue}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d.-]/g, "");
                              const numValue = value === "" ? "" : parseFloat(value);
                              handleDetailValueChange(detail.name, numValue === "" ? "" : (isNaN(numValue) ? currentValue : numValue));
                            }}
                            onFocus={() => {
                              setAmountCalculatorOpen(false);
                              setBottomListOpen(null);
                            }}
                            type="text"
                            inputProps={{ 
                              inputMode: "decimal",
                              pattern: "[0-9.-]*"
                            }}
                            fullWidth
                          />
                        ) : (
                          <TextField
                            label={detail.name}
                            value={currentValue}
                            onChange={(e) => {
                              handleDetailValueChange(detail.name, e.target.value.replace(/\n/g, ""));
                            }}
                            onFocus={() => {
                              setAmountCalculatorOpen(false);
                              setBottomListOpen(null);
                            }}
                            fullWidth
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </form>
      </Box>

      {/* Custom calculator for amount */}
      <MobileCalculator
        open={amountCalculatorOpen}
        initialValue={
          amountCalculatorOpen && calculatorExpression !== null && calculatorExpression !== undefined && calculatorExpression.trim() !== ""
            ? calculatorExpression
            : (formData.amount && formData.amount.trim() !== "" ? formData.amount : "0")
        }
        onValueChange={(value) => setCalculatorExpression(value)}
        onCheck={(value) => {
          setFormData((prev) => ({ ...prev, amount: value }));
          setAmountCalculatorOpen(false);
        }}
        onClose={() => setAmountCalculatorOpen(false)}
      />

      {/* Bottom list selection for Group, Currency, and detail lists (instead of dropdowns) */}
      {bottomListOpen === "group" && (
        <MobileBottomListSelect
          open={true}
          title="Select Group"
          items={accountGroups.map((g) => ({ value: g.name, label: g.name }))}
          selectedValue={formData.group}
          onSelect={(value) => {
            setFormData({ ...formData, group: value, details: {} });
            setShowAccountDetails(false);
            if (editingAccount) setInitialShowAccountDetails(false);
            setBottomListOpen(null);
          }}
          onClose={() => setBottomListOpen(null)}
        />
      )}
      {bottomListOpen === "currency" && (
        <MobileBottomListSelect
          open={true}
          title="Select Currency"
          items={[
            { value: defaultCurrency, label: defaultCurrency, secondary: "Default currency" },
            ...currencies
              .filter((c) => c.code !== defaultCurrency && c.rate != null)
              .map((c) => ({
                value: c.code,
                label: `${c.code} - ${c.name}`,
                secondary: c.symbol || undefined,
              })),
          ]}
          selectedValue={formData.currency}
          onSelect={(value) => {
            setFormData({ ...formData, currency: value });
            setBottomListOpen(null);
          }}
          onClose={() => setBottomListOpen(null)}
        />
      )}
      {bottomListOpen != null &&
        bottomListOpen.startsWith("detail:") &&
        (() => {
          const detailName = bottomListOpen.replace("detail:", "");
          const detail = getSelectedGroupDetails().find((d) => d.name === detailName);
          if (!detail || detail.type !== "list" || !detail.listItems?.length)
            return null;
          return (
            <MobileBottomListSelect
              open={true}
              title={detailName}
              items={detail.listItems.map((item) => ({ value: item, label: item }))}
              selectedValue={String(formData.details?.[detailName] ?? "")}
              onSelect={(value) => {
                handleDetailValueChange(detailName, value);
                setBottomListOpen(null);
              }}
              onClose={() => setBottomListOpen(null)}
            />
          );
        })()}

      {/* Submit Button - fixed above calculator when amount keyboard is open */}
      <MobileAccountSubmitButton
        isEditMode={isEditMode}
        isSubmitting={isSavingAccount}
        hasChanges={hasFormChanged()}
        isFormValid={isAccountFormValid}
        onCancel={handleBack}
        calculatorOpen={amountCalculatorOpen}
      />
    </Box>
  );
}

export default MobileAccountForm;
